import React, { useState, useEffect } from "react";
import { Modal, Form } from "react-bootstrap";
import CommonButton from "../../../components/commonButton";
import withModal from "../../../components/withModal";
import { useToast } from "../../../context/TostContext";
import InputField from "../../../components/form/InputField";
import CustomSelect from "../../../components/form/CustomSelect";

const AddEditAdConfigModalInner = ({ onClose, onSubmit, adConfig, isLoading, isView }) => {
  const [formData, setFormData] = useState({
    screen: "",
    screen_key: "",
    ad_type: "banner",
    provider: "admob",
    android_unit_id: "",
    ios_unit_id: "",
    is_active: true,
    meta_text: "",
  });
  const [metaError, setMetaError] = useState("");
  const toast = useToast();
  useEffect(() => {
    if (adConfig) {
      setFormData({
        screen: adConfig.screen || "",
        screen_key: adConfig.screen_key || "",
        ad_type: adConfig.ad_type || "banner",
        provider: adConfig.provider || "admob",
        android_unit_id: adConfig.android_unit_id || "",
        ios_unit_id: adConfig.ios_unit_id || "",
        is_active: adConfig.is_active ?? true,
        meta_text: adConfig.meta && Object.keys(adConfig.meta).length
          ? JSON.stringify(adConfig.meta, null, 2)
          : "",
      });
    } else {
      setFormData({
        screen: "",
        screen_key: "",
        ad_type: "banner",
        provider: "admob",
        android_unit_id: "",
        ios_unit_id: "",
        is_active: true,
        meta_text: "",
      });
    }
    setMetaError("");
  }, [adConfig]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isView) return;
    if (!formData.screen_key.trim()) {
      toast.error("Screen key is required");
      return;
    }
    // `meta` carries per-screen tuning the backend reads, e.g. session_minutes
    // and max_rewarded_per_day for the GIS rewarded unit.
    let meta = {};
    if (formData.meta_text.trim()) {
      try {
        meta = JSON.parse(formData.meta_text);
      } catch (err) {
        setMetaError("Meta must be valid JSON");
        toast.error("Meta must be valid JSON");
        return;
      }

      if (typeof meta !== "object" || Array.isArray(meta)) {
        setMetaError("Meta must be a JSON object");
        toast.error("Meta must be a JSON object");
        return;
      }
    }

    const { meta_text, ...rest } = formData;
    onSubmit({ ...rest, meta });
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Modal.Body className="grid row-gap-3">

        <InputField className="g-col-12"
          label={{ name: "Screen Name" }}
          input={{
            id: "screen",
            name: "screen",
            placeholder: "Ex: Home Screen",
            value: formData.screen,
            onChange: handleChange,
            disabled: isView
          }}
        />
        <InputField
          className="g-col-12"
          label={{ name: "Screen Key (Unique)" }}
          required={true}
          input={{
            id: "screen_key",
            name: "screen_key",
            placeholder: "Ex: home_bottom_banner",
            value: formData.screen_key,
            onChange: handleChange,
            disabled: !!adConfig || isView,
          }}
        />
        
        <CustomSelect
          className="g-col-6"
          label={{ name: "Ad Type" }}
          name="ad_type"
          value={formData.ad_type}
          onChange={handleSelectChange}
          options={[
            { value: "banner", label: "Banner" },
            { value: "native", label: "Native" },
            { value: "interstitial", label: "Interstitial" },
            { value: "rewarded", label: "Rewarded" },
          ]}
          isSearchable={false}
          isDisabled={isView}
        />
        <CustomSelect
          className="g-col-6"
          label={{ name: "Provider" }}
          name="provider"
          value={formData.provider}
          onChange={handleSelectChange}
          options={[
            { value: "admob", label: "AdMob" },
            { value: "facebook", label: "Facebook" },
          ]}
          isSearchable={false}
          isDisabled={isView}
        />
       
        <InputField
          className="g-col-12"
          label={{ name: "Android Unit ID" }}
          input={{
            id: "android_unit_id",
            name: "android_unit_id",
            placeholder: "Enter Android Unit ID",
            value: formData.android_unit_id,
            onChange: handleChange,
            disabled: isView,
          }}
        />
        <InputField
          className="g-col-12"
          label={{ name: "iOS Unit ID" }}
          input={{
            id: "ios_unit_id",
            name: "ios_unit_id",
            placeholder: "Enter iOS Unit ID",
            value: formData.ios_unit_id,
            onChange: handleChange,
            disabled: isView,
          }}
        />
        <div className="g-col-12">
          <Form.Label className="small fw-medium mb-1">Advanced settings (JSON)</Form.Label>
          <Form.Control
            as="textarea"
            rows={5}
            name="meta_text"
            className="font-monospace small"
            placeholder={'{\n  "session_minutes": 30,\n  "max_rewarded_per_day": 4\n}'}
            value={formData.meta_text}
            onChange={(e) => {
              setMetaError("");
              handleChange(e);
            }}
            disabled={isView}
            isInvalid={!!metaError}
          />
          {metaError ? (
            <div className="text-danger small mt-1">{metaError}</div>
          ) : (
            <small className="text-muted">
              Per-screen tuning read by the backend. The GIS rewarded unit uses
              session_minutes, max_rewarded_per_day and min_watch_seconds. Leave
              empty if the screen needs none.
            </small>
          )}
        </div>

        <div className="g-col-12">
          <Form.Check
            type="switch"
            id="is_active"
            name="is_active"
            label="Active"
            checked={formData.is_active}
            onChange={handleChange}
            disabled={isView}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <CommonButton
          variant="outline"
          title={isView ? "Close" : "Cancel"}
          onClick={onClose}
          type="button"
        />
        {!isView && (
          <CommonButton
            variant="primary"
            title={adConfig ? "Update" : "Save"}
            type="submit"
            isLoading={isLoading}
          />
        )}
      </Modal.Footer>
    </Form>
  );
};

const AddEditAdConfigModal = withModal(AddEditAdConfigModalInner);

export default AddEditAdConfigModal;
